import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Label } from '@/components/ui/label';
import { PasswordRequirements } from '@/components/password-requirements';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { isPasswordValid } from '@/lib/validation/password';
import {
  clearPasswordResetError,
  confirmPasswordReset,
  PasswordResetErrorCode,
  requestPasswordReset,
  resetPasswordResetState,
} from '@/features/password-reset';
import { getBusinessAuthMessage } from '@/features/auth';

const GENERIC_REQUEST_MESSAGE = getBusinessAuthMessage(
  'BusinessPasswordResetAccepted',
  'Check your email for the next step.',
);

function getErrorMessage(code: string | null, fallback: string | null): string | null {
  switch (code) {
    case PasswordResetErrorCode.InvalidResetCode:
      return getBusinessAuthMessage(code, 'The verification code is invalid or expired.');
    case PasswordResetErrorCode.InvalidPassword:
      return 'The new password does not meet the password requirements.';
    default:
      return getBusinessAuthMessage(code, fallback || 'Unable to complete the request.');
  }
}

function getRetryAfterSeconds(value: unknown): number | undefined {
  return Number.isSafeInteger(value) && Number(value) > 0 ? Number(value) : undefined;
}

export default function PasswordResetScreen() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    step,
    email: requestedEmail,
    isRequesting,
    isConfirming,
    error,
    errorCode,
  } = useAppSelector((state) => state.passwordReset);
  const locationEmail = (location.state as { email?: string } | null)?.email;
  const [email, setEmail] = useState(requestedEmail || locationEmail || '');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = window.setTimeout(() => setResendCountdown((value) => value - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendCountdown]);

  const displayError = getErrorMessage(errorCode, error);
  const trimmedEmail = email.trim();
  const passwordsMatch = newPassword === confirmPassword;
  const canConfirm =
    code.length === 6 && isPasswordValid(newPassword) && Boolean(confirmPassword) && passwordsMatch && !isConfirming;

  const handleRequest = async (event: FormEvent) => {
    event.preventDefault();
    if (!trimmedEmail || resendCountdown > 0 || isRequesting) return;

    try {
      const response = await dispatch(requestPasswordReset({ email: trimmedEmail })).unwrap();
      const retryAfterSeconds = getRetryAfterSeconds(response.retryAfterSeconds);
      if (retryAfterSeconds) setResendCountdown(retryAfterSeconds);
    } catch (err) {
      const retryAfterSeconds =
        typeof err === 'object' && err && 'retryAfterSeconds' in err
          ? getRetryAfterSeconds(err.retryAfterSeconds)
          : undefined;
      if (retryAfterSeconds) setResendCountdown(retryAfterSeconds);
      // The slice exposes the normalized backend error inline.
    }
  };

  const handleResend = async () => {
    if (!requestedEmail || resendCountdown > 0 || isRequesting) return;

    try {
      const response = await dispatch(requestPasswordReset({ email: requestedEmail })).unwrap();
      const retryAfterSeconds = getRetryAfterSeconds(response.retryAfterSeconds);
      if (retryAfterSeconds) setResendCountdown(retryAfterSeconds);
    } catch (err) {
      const retryAfterSeconds =
        typeof err === 'object' && err && 'retryAfterSeconds' in err
          ? getRetryAfterSeconds(err.retryAfterSeconds)
          : undefined;
      if (retryAfterSeconds) setResendCountdown(retryAfterSeconds);
      // The slice exposes the normalized backend error inline.
    }
  };

  const handleConfirm = async (event: FormEvent) => {
    event.preventDefault();
    if (!requestedEmail || !canConfirm) return;

    try {
      await dispatch(confirmPasswordReset({ email: requestedEmail, code, newPassword })).unwrap();
      setCode('');
      setNewPassword('');
      setConfirmPassword('');
      dispatch(resetPasswordResetState());
      toast.success('Password reset successfully. Sign in with your new password.');
      navigate('/login', { replace: true, state: { email: requestedEmail } });
    } catch {
      // The slice exposes the normalized backend error inline.
    }
  };

  const handleChangeEmail = () => {
    dispatch(resetPasswordResetState());
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
    setResendCountdown(0);
  };

  if (step === 'request') {
    return (
      <form className="flex flex-col gap-6" onSubmit={handleRequest}>
        <div className="flex flex-col items-center gap-2 text-center">
          <h1 className="text-2xl font-bold">Reset your password</h1>
          <p className="text-muted-foreground text-sm text-balance">
            Enter your business account email and we’ll send password reset instructions.
          </p>
        </div>

        {displayError && (
          <div role="alert" className="bg-destructive/15 text-destructive rounded-md p-3 text-sm font-medium">
            {displayError}
          </div>
        )}

        <div className="grid gap-3">
          <Label htmlFor="reset-email">Email</Label>
          <Input
            id="reset-email"
            type="email"
            autoComplete="email"
            placeholder="name@company.com"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setResendCountdown(0);
              dispatch(clearPasswordResetError());
            }}
            autoFocus
            required
          />
        </div>

        <Button type="submit" disabled={!trimmedEmail || resendCountdown > 0 || isRequesting}>
          {isRequesting
            ? 'Sending reset code…'
            : resendCountdown > 0
              ? `Try again in ${resendCountdown}s`
              : 'Send reset code'}
        </Button>

        <div className="text-center text-sm">
          <Link to="/login" className="text-primary hover:underline">
            Back to login
          </Link>
        </div>
      </form>
    );
  }

  return (
    <form className="flex flex-col gap-6" onSubmit={handleConfirm}>
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="text-2xl font-bold">Check your email</h1>
        <p className="text-muted-foreground text-sm text-balance">{GENERIC_REQUEST_MESSAGE}</p>
      </div>

      {displayError && (
        <div role="alert" className="bg-destructive/15 text-destructive rounded-md p-3 text-sm font-medium">
          {displayError}
        </div>
      )}

      <div className="grid gap-3">
        <Label htmlFor="reset-code">Six-digit reset code</Label>
        <div className="flex justify-center">
          <InputOTP
            id="reset-code"
            maxLength={6}
            value={code}
            onChange={(value) => {
              setCode(value.replace(/\D/g, ''));
              dispatch(clearPasswordResetError());
            }}
            autoFocus
          >
            <InputOTPGroup>
              {Array.from({ length: 6 }, (_, index) => (
                <InputOTPSlot key={index} index={index} />
              ))}
            </InputOTPGroup>
          </InputOTP>
        </div>
      </div>

      <div className="grid gap-3">
        <Label htmlFor="new-password">New password</Label>
        <div className="relative">
          <Input
            id="new-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            value={newPassword}
            onChange={(event) => {
              setNewPassword(event.target.value);
              dispatch(clearPasswordResetError());
            }}
            className="pr-10"
            required
          />
          <button
            type="button"
            aria-label={showPassword ? 'Hide new password' : 'Show new password'}
            onClick={() => setShowPassword((value) => !value)}
            className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2"
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {newPassword && <PasswordRequirements password={newPassword} />}
      </div>

      <div className="grid gap-3">
        <Label htmlFor="confirm-new-password">Confirm new password</Label>
        <div className="relative">
          <Input
            id="confirm-new-password"
            type={showConfirmPassword ? 'text' : 'password'}
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => {
              setConfirmPassword(event.target.value);
              dispatch(clearPasswordResetError());
            }}
            className="pr-10"
            required
          />
          <button
            type="button"
            aria-label={showConfirmPassword ? 'Hide confirmed password' : 'Show confirmed password'}
            onClick={() => setShowConfirmPassword((value) => !value)}
            className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2"
          >
            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {confirmPassword && !passwordsMatch && <p className="text-destructive text-sm">Passwords do not match.</p>}
      </div>

      <Button type="submit" disabled={!canConfirm}>
        {isConfirming ? 'Resetting password…' : 'Reset password'}
      </Button>

      <div className="space-y-3 text-center text-sm">
        {resendCountdown > 0 ? (
          <p className="text-muted-foreground">Resend code in {resendCountdown}s</p>
        ) : (
          <button type="button" className="text-primary hover:underline" disabled={isRequesting} onClick={handleResend}>
            {isRequesting ? 'Sending…' : 'Resend code'}
          </button>
        )}
        <div>
          <button type="button" className="text-muted-foreground hover:underline" onClick={handleChangeEmail}>
            Change email address
          </button>
        </div>
      </div>
    </form>
  );
}
